<script setup lang="ts">
  import type { MatrixCodeRevision } from '@/entities/Matrix';
  import { IconImage, Tooltip } from '@/shared/ui';
  import type { RecordModalTarget } from '@/widgets/record-modals';
  import HistoryIcon from '@/shared/assets/icons/HistoryIcon.svg';
  import SettingsIcon from '@/shared/assets/icons/SettingsIcon.svg';
  import PlusButtonIcon from '@/shared/assets/icons/PlusButtonIcon.svg';
  import type { MatrixRevisionGenerateAction } from '../../model/matrix-revision-generate-action';

  const props = defineProps<{
    row: MatrixCodeRevision;
    generateAction: MatrixRevisionGenerateAction;
  }>();

  const emit = defineEmits<{
    'open-history': [target: RecordModalTarget];
    'open-control': [target: RecordModalTarget];
    generate: [row: MatrixCodeRevision];
  }>();

  // Tippy binds `focus` to the reference and focus does not bubble: a wrapped button needs `focusin`.
  const TOOLTIP_TRIGGER = 'mouseenter focusin';

  const HISTORY_CONTROL_ACTIONS = [
    { key: 'history', label: 'Histórico', icon: HistoryIcon },
    { key: 'control', label: 'Controle', icon: SettingsIcon },
  ] as const;

  const GENERATE_LABEL = 'Gerar Registro';

  const handleIconAction = (key: (typeof HISTORY_CONTROL_ACTIONS)[number]['key']) => {
    const target: RecordModalTarget = {
      id: props.row.id,
      code: props.row.code,
      title: props.row.title,
      revision: props.row.revision,
    };

    if (key === 'history') emit('open-history', target);
    if (key === 'control') emit('open-control', target);
  };
</script>

<template>
  <td class="px-6 py-3 text-left align-middle">
    <div
      class="flex items-center gap-6"
      data-testid="matrix-revision-actions"
    >
      <div class="flex items-center gap-2 text-gray-xx-02">
        <Tooltip
          v-for="action in HISTORY_CONTROL_ACTIONS"
          :key="action.key"
          :content="action.label"
          :trigger="TOOLTIP_TRIGGER"
          placement="top"
          class="flex items-center"
        >
          <button
            type="button"
            class="flex items-center"
            :aria-label="action.label"
            :data-testid="`matrix-revision-action-${action.key}`"
            @click="handleIconAction(action.key)"
          >
            <IconImage
              :src="action.icon"
              alt=""
            />
          </button>
        </Tooltip>
      </div>
      <Tooltip
        v-if="props.generateAction === 'enabled' || props.generateAction === 'identify'"
        :content="GENERATE_LABEL"
        :trigger="TOOLTIP_TRIGGER"
        placement="top"
        class="flex items-center"
      >
        <button
          type="button"
          class="flex items-center text-[var(--theme-secondary)]"
          :aria-label="GENERATE_LABEL"
          :data-testid="`matrix-revision-generate-${props.row.id}`"
          @click="emit('generate', props.row)"
        >
          <IconImage
            :src="PlusButtonIcon"
            alt=""
          />
        </button>
      </Tooltip>
      <button
        v-else-if="props.generateAction === 'disabled'"
        type="button"
        class="flex cursor-not-allowed items-center text-[var(--theme-secondary)] opacity-50"
        :aria-label="GENERATE_LABEL"
        :data-testid="`matrix-revision-generate-${props.row.id}`"
        disabled
      >
        <IconImage
          :src="PlusButtonIcon"
          alt=""
        />
      </button>
    </div>
  </td>
</template>
